import React, { useState, useEffect, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Building2,
  Upload,
  CheckCircle,
  DollarSign,
  Shield,
  ArrowLeft,
  AlertTriangle,
  Save,
} from "lucide-react";
import { bondsAPI, walletAPI, filesAPI } from "../../services/api";
import { toast } from "sonner";
import LoadingSpinner from "../../components/common/LoadingSpinner";

import {
  extractResponseData,
  extractErrorMessage,
  extractSuccessMessage,
} from "../../utils/apiHelpers";

// Convert "YYYY-MM-DD" to Malaysia midnight in UTC
const toMalaysiaMidnightUTC = (dateString) => {
  if (!dateString) return null;
  // If it's already an ISO string with time component, return as-is
  if (typeof dateString === 'string' && dateString.includes('T')) {
    return dateString;
  }
  // Append Malaysia timezone offset (+08:00) to treat the date as Malaysia midnight
  // Then convert to ISO string (UTC)
  return new Date(dateString + 'T00:00:00+08:00').toISOString();
};

// Convert UTC date to Malaysia timezone date for display in date inputs
const fromUTCToMalaysiaDate = (utcDateString) => {
  if (!utcDateString) return null;
  // Parse the UTC date and convert to Malaysia timezone (UTC+8)
  const date = new Date(utcDateString);
  // Add 8 hours to convert from UTC to Malaysia time
  const malaysiaDate = new Date(date.getTime() + (8 * 60 * 60 * 1000));
  // Return in YYYY-MM-DD format for date input
  return malaysiaDate.toISOString().split('T')[0];
};

const EditBond = () => {
  const { bondId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedProductType, setSelectedProductType] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState({});
  // Token creation loading state removed (creation deferred until submission)
  const [showTokenLoading] = useState(false);

  const [formData, setFormData] = useState({});
  const [isTokenCreationInProgress, setIsTokenCreationInProgress] =
    useState(false);
  const tokenPollRef = useRef(null);
  const willCreateTokenRef = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      isESGBond: false,
      currency: "MYR",
      issuePrice: 100,
      minimumInvestmentValue: 1000,
    },
  });

  // Track whether initial form population has occurred to avoid resetting user-changed ESG state
  const hasInitializedRef = useRef(false);
  // Local explicit state for ESG selection to prevent disappearing upload section on re-renders
  const [isESGBondLocal, setIsESGBondLocal] = useState(false);

  // Register isESGBond manually so RHF validation still works while we control via local state
  useEffect(() => {
    // Register without required rule; default false acceptable
    register("isESGBond");
  }, [register]);

  // Keep form value in sync with local ESG state
  useEffect(() => {
    setValue("isESGBond", isESGBondLocal, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [isESGBondLocal, setValue]);

  // Fallback: if ESG state becomes undefined/null due to re-renders, force to false
  useEffect(() => {
    if (isESGBondLocal !== true && isESGBondLocal !== false) {
      setIsESGBondLocal(false);
    }
  }, [isESGBondLocal]);

  const steps = [
    {
      id: 1,
      title: "Bond Type",
      icon: Building2,
      description: "Select bond type",
    },
    {
      id: 2,
      title: "Basic Information",
      icon: FileText,
      description: "Enter bond details",
    },
    {
      id: 3,
      title: "Bond Details",
      icon: Shield,
      description: "Specific requirements",
    },
    {
      id: 4,
      title: "Token Information",
      icon: DollarSign,
      description: "Enter token details",
    },
    {
      id: 5,
      title: "Review & Submit",
      icon: CheckCircle,
      description: "Final review",
    },
  ];

  // Fetch bond data
  const {
    data: bondData,
    isPending: isLoading,
    error,
  } = useQuery({
    queryKey: ["bond", bondId],
    queryFn: () => bondsAPI.getBond(bondId),
    select: (data) => {
      const responseData = data.data;
      if (responseData && responseData.success && responseData.data) {
        return responseData.data.bond;
      }
      return null;
    },
    enabled: !!bondId,
  });

  // Fetch issuer wallet (for token creation step if token not yet created)
  const {
    data: walletData,
    isLoading: walletLoading,
    error: walletError,
  } = useQuery({
    queryKey: ["myWallet"],
    queryFn: walletAPI.getMyWallet,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch existing uploaded documents for this bond so they can be shown when editing
  const { data: existingFilesData } = useQuery({
    queryKey: ["bondDocuments", bondId],
    // Reuse bondsAPI.getBondDocuments which frontend already uses for bond docs
    queryFn: () => bondsAPI.getBondDocuments(bondId),
    enabled: !!bondId,
    staleTime: 5 * 60 * 1000,
  });

  const existingDocuments = useMemo(() => {
    const payload =
      existingFilesData?.data?.data || existingFilesData?.data || {};
    const docs = Array.isArray(payload.documents)
      ? payload.documents
      : payload.files;
    return Array.isArray(docs) ? docs : [];
  }, [existingFilesData]);

  const resolveApiPath = (path) => {
    const configuredBase = process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL.replace(/\/$/, "")
      : "";
    if (configuredBase) return `${configuredBase}${path}`;
    if (
      typeof window !== "undefined" &&
      window.location.hostname === "localhost"
    ) {
      return `http://localhost:5001${path}`;
    }
    return path;
  };

  const getFileIdentifier = (file) => file?.fileId || file?.id || file?._id;

  const isBrowserFileObject = (file) => {
    if (!file) return false;
    if (typeof File !== "undefined" && file instanceof File) return true;
    return (
      typeof file.lastModified === "number" && typeof file.size === "number"
    );
  };

  const getFileDisplayName = (file, fallback = "document") =>
    file?.name || file?.originalName || file?.filename || fallback;

  const withDownloadParam = (url) => {
    if (!url) return null;
    return url.includes("?") ? `${url}&download=1` : `${url}?download=1`;
  };

  const getPreviewUrl = (file) => {
    if (!file) return null;
    if (file.url) return file.url;
    if (file.fileUrl) return file.fileUrl;
    if (file.previewUrl) return file.previewUrl;
    if (file.pbRecordId)
      return resolveApiPath(`/api/files/pbrecord/${file.pbRecordId}/content`);
    const identifier = getFileIdentifier(file);
    if (!identifier) return null;
    return resolveApiPath(`/api/files/${identifier}/content`);
  };

  const getDownloadUrl = (file) => {
    if (!file) return null;
    const directUrl = file.url || file.fileUrl || file.previewUrl;
    if (directUrl) return withDownloadParam(directUrl);
    if (file.pbRecordId)
      return resolveApiPath(
        `/api/files/pbrecord/${file.pbRecordId}/content?download=1`
      );
    const identifier = getFileIdentifier(file);
    if (!identifier) return null;
    return resolveApiPath(`/api/files/${identifier}/content?download=1`);
  };

  const renderDocumentActions = (file, size = "sm") => {
    const previewUrl = getPreviewUrl(file);
    const downloadUrl = getDownloadUrl(file);
    if (!previewUrl && !downloadUrl) return null;
    const sizeClasses =
      size === "xs" ? "text-xs space-x-2" : "text-sm space-x-3";
    const downloadName = file?.originalName || file?.filename || "document";
    return (
      <div className={`flex items-center ${sizeClasses}`}>
        {previewUrl && (
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-primary-600 hover:text-primary-700"
          >
            Preview
          </a>
        )}
        {downloadUrl && (
          <a
            href={downloadUrl}
            download={downloadName}
            className="underline text-secondary-600 hover:text-secondary-700"
          >
            Download
          </a>
        )}
      </div>
    );
  };

  // Update bond mutation
  const updateBondMutation = useMutation({
    mutationFn: ({ id, data }) => bondsAPI.updateBond(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["bond", bondId]);
      queryClient.invalidateQueries(["bonds"]);
      toast.success(
        extractSuccessMessage(response, "Bond updated successfully")
      );
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, "Failed to update bond"));
    },
  });

  // Submit bond mutation
  const submitBondMutation = useMutation({
    mutationFn: bondsAPI.submitBond,
    onSuccess: async (response) => {
      queryClient.invalidateQueries(["bond", bondId]);
      queryClient.invalidateQueries(["bonds"]);

      const successMessage = extractSuccessMessage(
        response,
        "Bond submitted successfully! Your bond is now pending review from the Facility Agent."
      );
      toast.success(successMessage, { duration: 5000 });

      // If token creation is expected, start polling until created/failed before navigating
      if (willCreateTokenRef.current) {
        setIsTokenCreationInProgress(true);
        tokenPollRef.current = setInterval(async () => {
          try {
            const latest = await bondsAPI.getBond(bondId);
            const latestBond = latest?.data?.data?.bond || latest?.data?.bond;
            const td = latestBond?.tokenDetails;
            if (td?.tokenAddress && td?.tokenCreationStatus === "created") {
              clearInterval(tokenPollRef.current);
              tokenPollRef.current = null;
              setIsTokenCreationInProgress(false);
              toast.success("On-chain token created successfully.", {
                duration: 4000,
              });
              navigate(`/issuer/bonds/${bondId}`);
            } else if (td?.tokenCreationStatus === "failed") {
              clearInterval(tokenPollRef.current);
              tokenPollRef.current = null;
              setIsTokenCreationInProgress(false);
              toast.error(
                "Token creation failed. You can adjust details and resubmit.",
                { duration: 6000 }
              );
              navigate(`/issuer/bonds/${bondId}`);
            }
          } catch (e) {
            // Silent retry; do not spam user
          }
        }, 4000);
      } else {
        navigate(`/issuer/bonds/${bondId}`);
      }
    },
    onError: (error) => {
      toast.error(
        extractErrorMessage(error, "Failed to submit bond for approval")
      );
    },
  });

  // Populate form when bond data is loaded (initially) and avoid overwriting user-modified ESG state afterwards
  useEffect(() => {
    if (!bondData) return;

    const productType =
      bondData.productType === "Fixed Notes" ? "fixed_notes" : "sukuk";
    setSelectedProductType(productType);

    if (!hasInitializedRef.current) {
      if (currentStep === 1) setCurrentStep(2);

      const initialValues = {
        bondName: bondData.bondName,
        issueDate: fromUTCToMalaysiaDate(bondData.issueDate),
        maturityDate: fromUTCToMalaysiaDate(bondData.maturityDate),
        issueAmount: bondData.issueAmount,
        issuePrice: bondData.issuePrice,
        currency: bondData.currency,
        minimumInvestmentValue: bondData.minimumInvestmentValue,
        couponFrequency: bondData.couponFrequency,
        ...(productType === "fixed_notes"
          ? {
            couponRate: bondData.couponRate,
            creditRating: bondData.fixedNotesDetails?.creditRating,
            guarantor: bondData.fixedNotesDetails?.guarantor,
            collateral: bondData.fixedNotesDetails?.collateral,
            useOfProceeds: bondData.fixedNotesDetails?.useOfProceeds,
            riskFactors: bondData.fixedNotesDetails?.riskFactors,
            taxImplications: bondData.fixedNotesDetails?.taxImplications,
          }
          : {
            profitRate: bondData.couponRate,
            sukukStructureType:
              bondData.sukukDetails?.sukukType ||
              bondData.sukukDetails?.sukukStructure,
            islamicFinancingRating:
              bondData.sukukDetails?.islamicFinancialRating,
            assetDescription: bondData.sukukDetails?.underlyingAsset,
            sukukAssetType: bondData.sukukDetails?.sukukAssetType || "",
            assetLocation: bondData.sukukDetails?.assetLocation,
            assetOwnershipStructure:
              bondData.sukukDetails?.assetOwnershipStructure,
            shariahBoardName: bondData.sukukDetails?.shariahAdvisor,
            fatwaReference: bondData.sukukDetails?.fatwaReference,
            shariahApprovalDate:
              bondData.sukukDetails?.shariahApprovalDate?.split("T")[0],
            profitCalculationMethod:
              bondData.sukukDetails?.profitCalculationMethod,
            profitSharingRatio: bondData.sukukDetails?.profitSharingRatio,
            expectedReturn: bondData.sukukDetails?.expectedReturn,
            shariahCompliance: bondData.sukukDetails?.shariahCompliance,
          }),
        tokenWallet: bondData.tokenDetails?.wallet,
        tokenName: bondData.tokenDetails?.tokenName,
        tokenSymbol: bondData.tokenDetails?.symbol,
        tokenDecimals: bondData.tokenDetails?.decimal,
        faceValuePerToken: bondData.tokenDetails?.faceValuePerToken,
        isESGBond: bondData.isESGBond === true,
      };

      const cleanInitialValues = Object.fromEntries(
        Object.entries(initialValues).filter(
          ([_, v]) => v !== null && v !== undefined
        )
      );

      reset(cleanInitialValues);
      setFormData(cleanInitialValues);
      hasInitializedRef.current = true;
      // Initialize local ESG state from bond data once
      setIsESGBondLocal(bondData.isESGBond === true);
      return;
    }

    // Subsequent bondData updates: refresh fields but preserve user-selected ESG state if it differs
    const currentESG = isESGBondLocal;
    const updatedValues = {
      bondName: bondData.bondName,
      issueDate: fromUTCToMalaysiaDate(bondData.issueDate),
      maturityDate: fromUTCToMalaysiaDate(bondData.maturityDate),
      issueAmount: bondData.issueAmount,
      issuePrice: bondData.issuePrice,
      currency: bondData.currency,
      minimumInvestmentValue: bondData.minimumInvestmentValue,
      couponFrequency: bondData.couponFrequency,
      ...(productType === "fixed_notes"
        ? {
          couponRate: bondData.couponRate,
          creditRating: bondData.fixedNotesDetails?.creditRating,
          guarantor: bondData.fixedNotesDetails?.guarantor,
          collateral: bondData.fixedNotesDetails?.collateral,
          useOfProceeds: bondData.fixedNotesDetails?.useOfProceeds,
          riskFactors: bondData.fixedNotesDetails?.riskFactors,
          taxImplications: bondData.fixedNotesDetails?.taxImplications,
        }
        : {
          profitRate: bondData.couponRate,
          sukukStructureType:
            bondData.sukukDetails?.sukukType ||
            bondData.sukukDetails?.sukukStructure,
          islamicFinancingRating:
            bondData.sukukDetails?.islamicFinancialRating,
          assetDescription: bondData.sukukDetails?.underlyingAsset,
          sukukAssetType: bondData.sukukDetails?.sukukAssetType || "",
          assetLocation: bondData.sukukDetails?.assetLocation,
          assetOwnershipStructure:
            bondData.sukukDetails?.assetOwnershipStructure,
          shariahBoardName: bondData.sukukDetails?.shariahAdvisor,
          fatwaReference: bondData.sukukDetails?.fatwaReference,
          shariahApprovalDate:
            fromUTCToMalaysiaDate(bondData.sukukDetails?.shariahApprovalDate),
          profitCalculationMethod:
            bondData.sukukDetails?.profitCalculationMethod,
          profitSharingRatio: bondData.sukukDetails?.profitSharingRatio,
          expectedReturn: bondData.sukukDetails?.expectedReturn,
          shariahCompliance: bondData.sukukDetails?.shariahCompliance,
        }),
      tokenWallet: bondData.tokenDetails?.wallet,
      tokenName: bondData.tokenDetails?.tokenName,
      tokenSymbol: bondData.tokenDetails?.symbol,
      tokenDecimals: bondData.tokenDetails?.decimal,
      faceValuePerToken: bondData.tokenDetails?.faceValuePerToken,
      isESGBond:
        currentESG !== undefined && currentESG !== bondData.isESGBond
          ? currentESG
          : bondData.isESGBond === true,
    };

    Object.entries(updatedValues).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        // Avoid calling setValue redundantly for unchanged values
        if (watch(key) !== value) setValue(key, value, { shouldDirty: false });
      }
    });
    setFormData((prev) => ({ ...prev, ...updatedValues }));
    // If first initialization, also set local ESG state from bond data
    if (!hasInitializedRef.current) {
      setIsESGBondLocal(bondData.isESGBond === true);
    }
  }, [bondData, reset, setValue, watch, currentStep]);

  // When existing files are fetched, map them into uploadedFiles state so UI can reflect already-uploaded docs
  useEffect(() => {
    if (!Array.isArray(existingDocuments) || existingDocuments.length === 0)
      return;
    const next = {};

    existingDocuments.forEach((file) => {
      const category = file?.metadata?.category || file?.category;
      if (!category) return;

      // For categories that support multiple files in CreateBond (e.g. legalDocuments), keep an array
      if (category === "legalDocuments") {
        if (!next[category]) next[category] = [];
        next[category].push(file);
      } else {
        // Single-file categories: keep last one, or first if nothing set yet
        if (!next[category]) {
          next[category] = file;
        }
      }
    });

    setUploadedFiles((prev) => ({ ...next, ...prev }));
  }, [existingDocuments]);

  // Populate form with stored data when navigating between steps
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      const currentStepFields = {};

      // Populate fields relevant to current step
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== undefined) {
          currentStepFields[key] = formData[key];
        }
      });

      // Update form values without resetting the entire form
      Object.keys(currentStepFields).forEach((key) => {
        setValue(key, currentStepFields[key]);
      });
    }
  }, [currentStep, formData, setValue]);

  // Ensure token fields are populated when token already exists and user reaches Step 4
  useEffect(() => {
    if (currentStep === 4 && bondData?.tokenDetails) {
      // Only set if not already set (avoid overwriting user edits)
      const existing = bondData.tokenDetails;
      const currentTokenName = watch("tokenName");
      if (!currentTokenName && existing.tokenName)
        setValue("tokenName", existing.tokenName);
      const currentSymbol = watch("tokenSymbol");
      if (!currentSymbol && (existing.symbol || existing.tokenSymbol))
        setValue("tokenSymbol", existing.symbol || existing.tokenSymbol);
      const currentDecimals = watch("tokenDecimals");
      if (
        (currentDecimals === undefined ||
          currentDecimals === "" ||
          currentDecimals === null) &&
        existing.decimal !== undefined
      )
        setValue("tokenDecimals", existing.decimal);
      const currentFace = watch("faceValuePerToken");
      if (
        (currentFace === undefined ||
          currentFace === "" ||
          currentFace === null) &&
        existing.faceValuePerToken !== undefined
      )
        setValue("faceValuePerToken", existing.faceValuePerToken);
      const currentWallet = watch("tokenWallet");
      if (!currentWallet && existing.wallet)
        setValue("tokenWallet", existing.wallet);
    }
  }, [currentStep, bondData, setValue, watch]);

  const handleProductTypeSelection = (productType) => {
    setSelectedProductType(productType);
    const apiProductType =
      productType === "fixed_notes" ? "Fixed Notes" : "Sukuk";
    setFormData((prev) => ({
      ...prev,
      productType: apiProductType,
    }));
    setCurrentStep(2);
  };

  const handleNext = (data) => {
    if (currentStep === 1) {
      return; // Handled by product type selection
    }

    // Store form data from current step locally (no API update)
    // Combine data from handleSubmit with current watch values
    const currentFormData = watch();

    // Preserve previously entered token fields if navigating back from step 4 to step 3
    const tokenFieldNames = [
      "tokenName",
      "tokenSymbol",
      "tokenDecimals",
      "faceValuePerToken",
      "tokenWallet",
    ];
    const preservedTokenFields = {};
    tokenFieldNames.forEach((f) => {
      if (
        formData[f] &&
        (currentFormData[f] === undefined ||
          currentFormData[f] === "" ||
          currentFormData[f] === null)
      ) {
        preservedTokenFields[f] = formData[f];
      }
    });

    const merged = { ...currentFormData, ...data, ...preservedTokenFields };

    const updatedFormData = { ...formData, ...merged, step: currentStep };
    setFormData(updatedFormData);

    // If on token creation step and token not yet created, call API to persist token details (which should trigger backend token creation process) then advance.
    if (currentStep === 4 && !tokenLocked) {
      // Build payload excluding empty token fields to avoid validation errors
      const payload = { ...merged };
      tokenFieldNames.forEach((f) => {
        if (
          payload[f] === "" ||
          payload[f] === undefined ||
          payload[f] === null
        ) {
          delete payload[f];
        }
      });
      // Persist token details only; creation deferred until submission
      updateBondMutation.mutate(
        {
          id: bondId,
          data: { ...payload, step: currentStep },
        },
        {
          onSuccess: () => {
            setCurrentStep(5);
          },
        }
      );
      return;
    }

    // Move to next step without saving to API for other steps
    if (currentStep < 5) setCurrentStep(currentStep + 1);
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateBondData = (data) => {
    const organizedData = organizeFormData(data);

    // Update bond with properly organized data
    updateBondMutation.mutate({
      id: bondId,
      data: organizedData,
    });
  };

  const handleSaveAndExit = (data) => {
    // Combine all stored form data with current form data and submitted data
    const currentFormData = watch();
    const updatedFormData = {
      ...formData,
      ...currentFormData,
      ...data,
    };
    updateBondData(updatedFormData);

    // Navigate back to bond details or management after saving
    toast.promise(
      new Promise((resolve) => {
        setTimeout(() => {
          navigate(`/issuer/bonds/${bondId}`);
          resolve();
        }, 1000);
      }),
      {
        loading: "Saving changes...",
        success: "Changes saved successfully!",
        error: "Failed to save changes",
      }
    );
  };

  const handleSubmitForApproval = (data) => {
    // First save the bond with current data
    const currentFormData = watch();
    const updatedFormData = {
      ...formData,
      ...currentFormData,
      ...data,
    };

    // Save bond data first, then submit for approval
    const organizedData = organizeFormData(updatedFormData);

    // Determine if token creation should occur (token details present & not locked)
    const willCreateToken =
      !tokenLocked &&
      watch("tokenName") &&
      watch("tokenSymbol") &&
      watch("tokenDecimals") !== undefined &&
      watch("faceValuePerToken") &&
      watch("tokenWallet");
    willCreateTokenRef.current = !!willCreateToken;

    updateBondMutation.mutate(
      {
        id: bondId,
        data: organizedData,
      },
      {
        onSuccess: () => {
          submitBondMutation.mutate(bondId);
        },
      }
    );
  };

  const organizeFormData = (data) => {
    // Organize data based on product type for proper backend structure
    const organizedData = { ...data };

    if (selectedProductType === "sukuk") {
      // Organize Sukuk-specific fields under sukukDetails
      organizedData.sukukDetails = {
        sukukType: data.sukukStructureType,
        islamicFinancialRating: data.islamicFinancingRating,
        underlyingAsset: data.assetDescription,
        sukukStructure: data.sukukStructureType,
        shariahAdvisor: data.shariahBoardName,
        profitSharingRatio: data.profitSharingRatio,
        expectedReturn: data.expectedReturn,
        shariahCompliance: data.shariahCompliance,
        // Additional fields from the form
        sukukAssetType: data.sukukAssetType || null,
        assetLocation: data.assetLocation,
        assetOwnershipStructure: data.assetOwnershipStructure,
        fatwaReference: data.fatwaReference,
        profitCalculationMethod: data.profitCalculationMethod,
        shariahApprovalDate: data.shariahApprovalDate,
      };

      // Map profitRate to couponRate for consistency
      organizedData.couponRate = data.profitRate || data.couponRate;

      // Remove flat sukuk fields
      delete organizedData.sukukStructureType;
      delete organizedData.islamicFinancingRating;
      delete organizedData.profitRate;
      delete organizedData.assetDescription;
      delete organizedData.shariahBoardName;
      delete organizedData.profitSharingRatio;
      delete organizedData.expectedReturn;
      delete organizedData.shariahCompliance;
      delete organizedData.sukukAssetType;
      delete organizedData.assetLocation;
      delete organizedData.assetOwnershipStructure;
      delete organizedData.fatwaReference;
      delete organizedData.profitCalculationMethod;
      delete organizedData.shariahApprovalDate;
    } else if (selectedProductType === "fixed_notes") {
      // Organize Fixed Notes-specific fields under fixedNotesDetails
      organizedData.fixedNotesDetails = {
        creditRating: data.creditRating,
        guarantor: data.guarantor,
        collateral: data.collateral,
        useOfProceeds: data.useOfProceeds,
        riskFactors: data.riskFactors,
        taxImplications: data.taxImplications,
      };

      // Remove flat fixed notes fields
      delete organizedData.creditRating;
      delete organizedData.guarantor;
      delete organizedData.collateral;
      delete organizedData.useOfProceeds;
      delete organizedData.riskFactors;
      delete organizedData.taxImplications;
    }

    // Convert ESG bond string to boolean for database storage
    if (organizedData.hasOwnProperty("isESGBond")) {
      organizedData.isESGBond =
        organizedData.isESGBond === true || organizedData.isESGBond === "true";
    }

    // Convert dates to Malaysia midnight UTC before sending
    if (organizedData.issueDate) {
      organizedData.issueDate = toMalaysiaMidnightUTC(organizedData.issueDate);
    }
    if (organizedData.maturityDate) {
      organizedData.maturityDate = toMalaysiaMidnightUTC(organizedData.maturityDate);
    }

    return organizedData;
  };

  const handleFileUpload = (event, fieldName) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFiles((prev) => ({
        ...prev,
        [fieldName]: file,
      }));
      // Auto-upload ESG certification immediately when selected
      if (fieldName === "esgCertification" && bondId) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", "document");
        formData.append(
          "relatedTo",
          JSON.stringify({ module: "bonds", id: bondId })
        );
        formData.append(
          "metadata",
          JSON.stringify({
            category: "esgCertification",
            originalName: file.name,
          })
        );
        filesAPI
          .uploadFile(formData)
          .then(() => {
            toast.success("ESG certification uploaded");
          })
          .catch((err) => {
            console.error("ESG certification upload failed:", err);
            toast.error("Failed to upload ESG certification");
          });
      }
    }
  };

  const handleFileDelete = (fieldName) => {
    setUploadedFiles((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
    if (typeof setValue === "function") {
      setValue(fieldName, undefined);
    }
  };

  // Loading state
  if (isLoading) {
    return <LoadingSpinner size="lg" text="Loading bond details..." />;
  }

  // Error state
  if (error) {
    console.error("Bond fetch error:", error);
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h2 className="mb-2 text-lg font-medium text-gray-900">
            Loading Failed
          </h2>
          <p className="mb-4 text-gray-600">
            Unable to load bond details. Please try again later.
          </p>
          <button
            onClick={() => navigate("/issuer/bonds")}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
          >
            Back to Bond List
          </button>
        </div>
      </div>
    );
  }

  // Bond not found
  if (!bondData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <h2 className="mb-2 text-lg font-medium text-gray-900">
            Bond Not Found
          </h2>
          <p className="mb-4 text-gray-600">
            The specified bond does not exist or you don't have permission to
            edit it.
          </p>
          <button
            onClick={() => navigate("/issuer/bonds")}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
          >
            Back to Bond List
          </button>
        </div>
      </div>
    );
  }

  // Check if bond can be edited (only draft, pending L1, and pending L2 bonds should be editable)
  const editableStatuses = ["Draft", "Pending L1", "Pending L2"];
  if (!editableStatuses.includes(bondData.status)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
          <h2 className="mb-2 text-lg font-medium text-gray-900">
            Cannot Edit Bond
          </h2>
          <p className="mb-4 text-gray-600">
            This bond cannot be edited because it has been approved. Only draft
            bonds and bonds pending approval can be modified.
          </p>
          <div className="space-x-3">
            <button
              onClick={() => navigate(`/issuer/bonds/${bondId}`)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
            >
              View Bond Details
            </button>
            <button
              onClick={() => navigate("/issuer/bonds")}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
            >
              Back to Bond List
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Token immutability: treat token as deployed if we have an address or creation status 'created'
  const tokenLocked = !!(
    bondData?.tokenDetails?.tokenAddress ||
    bondData?.tokenDetails?.contractAddress ||
    bondData?.tokenDetails?.tokenCreationStatus === "created"
  );
  // Allow submission for Draft bonds and Pending L2 bonds (resubmission after rejection)
  const canSubmitForApproval =
    bondData?.status === "Draft" || bondData?.status === "Pending L2";

  // Basic info completeness checker (mirrors CreateBond logic)
  const areBasicFieldsComplete = () => {
    const values = watch();
    const required = [
      "bondName",
      "locationCode",
      "scriplessFlag",
      "isESGBond",
      "issueDate",
      "maturityDate",
      "issueAmount",
      "issuePrice",
      "currency",
      "minimumInvestmentValue",
    ];
    // All present (non-empty / non-undefined); boolean false is valid for isESGBond
    for (const key of required) {
      const val = values[key];
      if (
        val === undefined ||
        val === null ||
        (typeof val === "string" && val.trim() === "")
      )
        return false;
    }
    // Numeric validations
    const issueAmountNum = Number(values.issueAmount);
    if (!Number.isFinite(issueAmountNum) || issueAmountNum < 1000) return false;
    const issuePriceNum = Number(values.issuePrice);
    if (
      !Number.isFinite(issuePriceNum) ||
      issuePriceNum < 50 ||
      issuePriceNum > 150
    )
      return false;
    const minInv = Number(values.minimumInvestmentValue);
    if (!Number.isFinite(minInv) || minInv < 1000) return false;

    // ESG certification upload is optional regardless of ESG selection
    return true;
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-center">
          <div
            className={`flex flex-col items-center ${index > 0 ? "ml-8" : ""}`}
          >
            <div
              className={`
              w-12 h-12 rounded-full flex items-center justify-center border-2 cursor-pointer
              ${currentStep >= step.id
                  ? "bg-primary-500 border-primary-500 text-white"
                  : "bg-white border-gray-400 text-gray-400"
                }
            `}
              onClick={() => {
                // Store current form data before navigating
                const currentFormData = watch();
                setFormData((prev) => ({ ...prev, ...currentFormData }));
                setCurrentStep(step.id);
              }}
            >
              <step.icon className="w-5 h-5" />
            </div>
            <div className="mt-2 text-center">
              <div className="text-sm font-medium text-secondary-900">
                {step.title}
              </div>
              <div className="text-xs text-secondary-500">
                {step.description}
              </div>
            </div>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`
              w-16 h-0.5 mt-6 ml-4
              ${currentStep > step.id ? "bg-primary-500" : "bg-gray-400"}
            `}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8 text-center">
        <h2 className="mb-4 text-3xl font-bold text-secondary-900">
          Bond Type
        </h2>
        <p className="text-lg text-secondary-600">
          Current bond type:{" "}
          <span className="font-semibold">{bondData.productType === "Fixed Notes" ? "Conventional" : bondData.productType}</span>
        </p>
        <p className="mt-2 text-sm text-gray-500">
          Bond type cannot be changed when editing
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Conventional Card */}
        <div
          className={`
            border-2 rounded-lg p-8 opacity-50 cursor-not-allowed
            ${selectedProductType === "fixed_notes"
              ? "border-primary-500 bg-primary-50"
              : "border-secondary-200 bg-white"
            }
          `}
        >
          <div className="text-center">
            <DollarSign className="w-16 h-16 mx-auto mb-4 text-primary-500" />
            <h3 className="mb-4 text-2xl font-bold text-secondary-900">
              Conventional
            </h3>
          </div>
          <p className="mb-6 text-secondary-600">
            Create an asset-backed security compliant digital bond.
          </p>
        </div>

        {/* Sukuk Card */}
        <div
          className={`
            border-2 rounded-lg p-8 opacity-50 cursor-not-allowed
            ${selectedProductType === "sukuk"
              ? "border-primary-500 bg-primary-50"
              : "border-secondary-200 bg-white"
            }
          `}
        >
          <div className="text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-success-500" />
            <h3 className="mb-4 text-2xl font-bold text-secondary-900">
              Sukuk (Islamic Bond)
            </h3>
            <p className="mb-6 text-secondary-600">
              Create an asset-backed security compliant with Shariah principles
              for Islamic finance.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-8">
        <button
          type="button"
          onClick={() => setCurrentStep(2)}
          className="flex items-center btn-primary"
        >
          Next
          <ChevronRight className="w-4 h-4 ml-2" />
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="max-w-4xl mx-auto">
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-medium text-dark-gray">
            Basic Bond Information
          </h2>
          <p className="mt-1 text-sm text-secondary-600">
            Update the core details for your{" "}
            {selectedProductType === "fixed_notes" ? "Conventional" : "Sukuk"}
          </p>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit(handleNext)} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Bond Name */}
              <div className="md:col-span-2">
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Bond Name *
                </label>
                <input
                  type="text"
                  {...register("bondName", {
                    required: "Bond name is required",
                    minLength: {
                      value: 3,
                      message: "Bond name must be at least 3 characters",
                    },
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Khazanah Conventional Bond 2026"
                />
                {errors.bondName && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.bondName.message}
                  </p>
                )}
              </div>

              {/* Location Code */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Location Code *
                </label>
                <input
                  type="text"
                  {...register("locationCode", {
                    required: "Location code is required",
                    minLength: {
                      value: 2,
                      message: "Location code must have at least 2 characters",
                    },
                    maxLength: {
                      value: 10,
                      message: "Location code must be 10 characters or fewer",
                    },
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., MLY"
                  style={{ textTransform: "uppercase" }}
                  onInput={(e) =>
                    (e.target.value = e.target.value.toUpperCase())
                  }
                  defaultValue={bondData?.locationCode || ""}
                />
                {errors.locationCode && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.locationCode.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-secondary-500">
                  Regulatory location code used for reporting and settlement.
                </p>
              </div>

              {/* Scripless Issuance */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Scripless Issuance *
                </label>
                <select
                  {...register("scriplessFlag", {
                    setValueAs: (value) => {
                      if (value === "") return undefined; // not selected
                      return value === "true";
                    },
                    validate: (value) =>
                      value !== undefined ||
                      "Please specify if the bond is scripless",
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  defaultValue={
                    bondData?.scriplessFlag === true
                      ? "true"
                      : bondData?.scriplessFlag === false
                        ? "false"
                        : ""
                  }
                >
                  <option value="" disabled>
                    Select option
                  </option>
                  <option value="true">Yes, the bond will be scripless</option>
                  <option value="false">
                    No, physical certificates will be issued
                  </option>
                </select>
                {errors.scriplessFlag && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.scriplessFlag.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-secondary-500">
                  Scripless bonds are recorded electronically without physical
                  certificates.
                </p>
              </div>

              {/* ESG Bond Classification */}
              <div className="space-y-4 md:col-span-2">
                <div>
                  <label className="block mb-2 text-sm font-medium text-dark-gray">
                    ESG/Sustainability Bond{" "}
                    <span className="required-asterisk">*</span>
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="isESGBond"
                        value="true"
                        checked={isESGBondLocal === true}
                        onChange={() => setIsESGBondLocal(true)}
                        className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-light-gray"
                      />
                      <span className="ml-2 text-sm text-dark-gray">Yes</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="isESGBond"
                        value="false"
                        checked={isESGBondLocal === false}
                        onChange={() => setIsESGBondLocal(false)}
                        className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-light-gray"
                      />
                      <span className="ml-2 text-sm text-dark-gray">No</span>
                    </label>
                  </div>
                </div>

                {/* ESG Certification Upload - always visible and optional */}
                <div className="mt-2">
                  <label className="block mb-2 text-sm font-medium text-dark-gray">
                    Upload ESG Certification (PDF, DOC, PNG, JPG){" "}
                    <span className="text-xs font-normal text-secondary-500">
                      (Optional)
                    </span>
                  </label>
                  <div className="flex flex-col space-y-2">
                    <div className="flex items-center space-x-3">
                      <input
                        id="esgCertificationFile"
                        type="file"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        onChange={(e) =>
                          handleFileUpload(e, "esgCertification")
                        }
                        className="hidden"
                      />
                      <label
                        htmlFor="esgCertificationFile"
                        className="px-4 py-2 text-sm font-semibold border rounded-md cursor-pointer bg-primary-50 text-primary-700 hover:bg-primary-100 border-primary-200"
                      >
                        Browse...
                      </label>
                    </div>
                    {uploadedFiles?.esgCertification && (
                      <div className="flex flex-col text-sm sm:flex-row sm:items-center sm:justify-between">
                        <span className="max-w-xs truncate text-secondary-700">
                          {getFileDisplayName(
                            uploadedFiles.esgCertification,
                            "ESG certification"
                          )}
                        </span>
                        <div className="flex items-center mt-2 space-x-3 sm:mt-0">
                          {isBrowserFileObject(
                            uploadedFiles.esgCertification
                          ) ? (
                            <button
                              type="button"
                              onClick={() =>
                                handleFileDelete("esgCertification")
                              }
                              className="underline text-error-600 hover:text-error-700"
                            >
                              Remove
                            </button>
                          ) : (
                            renderDocumentActions(
                              uploadedFiles.esgCertification,
                              "xs"
                            )
                          )}
                        </div>
                      </div>
                    )}
                    {/* If existing ESG file from server is present but no upload tracked in state */}
                    {!uploadedFiles?.esgCertification &&
                      existingDocuments.length > 0 &&
                      (() => {
                        const esgFile = existingDocuments.find(
                          (f) =>
                            (f?.metadata?.category || f?.category) ===
                            "esgCertification"
                        );
                        if (!esgFile) return null;
                        return (
                          <div className="flex items-center justify-between text-sm text-secondary-700">
                            <span className="max-w-xs truncate">
                              {getFileDisplayName(
                                esgFile,
                                "Existing ESG certification"
                              )}
                            </span>
                            {renderDocumentActions(esgFile, "xs")}
                          </div>
                        );
                      })()}
                  </div>
                  <p className="mt-1 text-xs text-secondary-500">
                    You may upload evidence of ESG/Sustainability certification
                    to support classification.
                  </p>
                </div>
              </div>

              {/* Issue Date */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Issue Date *
                </label>
                <input
                  type="date"
                  {...register("issueDate", {
                    required: "Issue date is required",
                  })}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
                {errors.issueDate && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.issueDate.message}
                  </p>
                )}
              </div>

              {/* Maturity Date */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Maturity Date *
                </label>
                <input
                  type="date"
                  {...register("maturityDate", {
                    required: "Maturity date is required",
                  })}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
                {errors.maturityDate && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.maturityDate.message}
                  </p>
                )}
              </div>

              {/* Issue Amount */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Issue Amount *
                </label>
                <input
                  type="number"
                  step="1"
                  onKeyDown={(e) => {
                    if (e.key === '.' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const pastedData = e.clipboardData.getData('text');
                    if (!/^\d+$/.test(pastedData)) {
                      e.preventDefault();
                    }
                  }}
                  {...register("issueAmount", {
                    required: "Issue amount is required",
                    min: {
                      value: 1000,
                      message: "Minimum amount is MYR 1,000",
                    },
                    valueAsNumber: true,
                    validate: {
                      isInteger: (value) => Number.isInteger(Number(value)) || "Issue amount must be a whole number",
                    },
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="1000000"
                  defaultValue={bondData?.issueAmount}
                />
                {errors.issueAmount && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.issueAmount.message}
                  </p>
                )}
              </div>

              {/* Issue Price */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Issue Price (%) *
                </label>
                <input
                  type="number"
                  step="1"
                  onKeyDown={(e) => {
                    if (e.key === '.' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                      e.preventDefault();
                    }
                  }}
                  {...register("issuePrice", {
                    required: "Issue price is required",
                    min: { value: 50, message: "Minimum price is 50%" },
                    max: { value: 150, message: "Maximum price is 150%" },
                    setValueAs: (value) => {
                      if (value === "" || value === null || value === undefined)
                        return undefined;
                      const num = Number(value);
                      if (!Number.isFinite(num)) return undefined;
                      return Math.round(num); // enforce integer
                    },
                    validate: (value) =>
                      Number.isInteger(value) ||
                      "Issue price must be an integer",
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="100"
                  defaultValue={bondData?.issuePrice || 100}
                  onInput={(e) => {
                    // Strip decimals live to maintain integer constraint
                    if (e.target.value.includes(".")) {
                      e.target.value = e.target.value.split(".")[0];
                    }
                  }}
                />
                {errors.issuePrice && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.issuePrice.message}
                  </p>
                )}
              </div>

              {/* Currency */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Currency *
                </label>
                <select
                  {...register("currency", {
                    required: "Currency is required",
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="MYR">Malaysian Ringgit (MYR)</option>
                </select>
                {errors.currency && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.currency.message}
                  </p>
                )}
              </div>

              {/* Minimum Investment Value */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Minimum Investment Unit (MYR) *
                </label>
                <input
                  type="number"
                  onKeyDown={(e) => {
                    if (e.key === 'e' || e.key === 'E') {
                      e.preventDefault();
                    }
                  }}
                  {...register("minimumInvestmentValue", {
                    required: "Minimum investment unit is required",
                    min: { value: 1000, message: "Minimum unit is MYR 1,000" },
                    valueAsNumber: true,
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="1000"
                  defaultValue={bondData?.minimumInvestmentValue || 1000}
                />
                {errors.minimumInvestmentValue && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.minimumInvestmentValue.message}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={handlePrevious}
                className="flex items-center btn-secondary"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleSubmit(handleSaveAndExit)}
                  className="flex items-center btn-secondary"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save & Exit
                </button>
                <button
                  type="submit"
                  disabled={
                    updateBondMutation.isPending || !areBasicFieldsComplete()
                  }
                  className="flex items-center px-6 py-3 transition-colors border rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{
                    fontFamily: '"Roboto", sans-serif',
                    fontWeight: 700,
                    backgroundColor:
                      updateBondMutation.isPending || !areBasicFieldsComplete()
                        ? "var(--color-medium-gray)"
                        : "var(--color-primary)",
                    borderColor:
                      updateBondMutation.isPending || !areBasicFieldsComplete()
                        ? "var(--color-medium-gray)"
                        : "var(--color-primary)",
                    color:
                      updateBondMutation.isPending || !areBasicFieldsComplete()
                        ? "var(--color-dark-gray)"
                        : "white",
                    cursor:
                      updateBondMutation.isPending || !areBasicFieldsComplete()
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      updateBondMutation.isPending || !areBasicFieldsComplete()
                        ? 0.8
                        : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (
                      updateBondMutation.isPending ||
                      !areBasicFieldsComplete()
                    )
                      return;
                    e.target.style.backgroundColor = "var(--color-white)";
                    e.target.style.color = "var(--color-primary)";
                    e.target.style.borderColor = "var(--color-primary)";
                  }}
                  onMouseLeave={(e) => {
                    if (
                      updateBondMutation.isPending ||
                      !areBasicFieldsComplete()
                    )
                      return;
                    e.target.style.backgroundColor = "var(--color-primary)";
                    e.target.style.color = "white";
                    e.target.style.borderColor = "var(--color-primary)";
                  }}
                >
                  {updateBondMutation.isPending ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // Similar implementation for steps 3, 4, and 5 would follow the same pattern as in CreateBond
  // but with the existing data pre-populated and save functionality

  const renderStep3 = () => {
    if (selectedProductType === "fixed_notes") {
      return renderFixedNotesStep();
    } else {
      return renderSukukStep();
    }
  };

  const renderFixedNotesStep = () => (
    <div className="max-w-4xl mx-auto">
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-medium text-dark-gray">
            Conventional Details
          </h2>
          <p className="mt-1 text-sm text-secondary-600">
            Update the specific terms for your Conventional bond
          </p>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit(handleNext)} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Coupon Rate */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Coupon Rate (Annual %) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  onKeyDown={(e) => {
                    if (e.key === 'e' || e.key === 'E') {
                      e.preventDefault();
                    }
                  }}
                  {...register("couponRate", {
                    required: "Coupon rate is required",
                    min: { value: 0, message: "Rate cannot be negative" },
                    max: { value: 100, message: "Rate cannot exceed 100%" },
                    validate: {
                      isNumber: (value) => {
                        const num = parseFloat(value);
                        return (
                          (!isNaN(num) && isFinite(num)) ||
                          "Please enter a valid number (integers or decimals only)"
                        );
                      },
                    },
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="5.0"
                  onKeyPress={(e) => {
                    // Allow only numbers, decimal point, and control keys
                    if (
                      !/[\d.]/.test(e.key) &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      e.key !== "ArrowLeft" &&
                      e.key !== "ArrowRight"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
                {errors.couponRate && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.couponRate.message}
                  </p>
                )}
              </div>

              {/* Payment Frequency */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Coupon Payment Frequency *
                </label>
                <select
                  {...register("couponFrequency", {
                    required: "Payment frequency is required",
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select frequency</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Semi-Annually">Semi-Annually</option>
                  <option value="Annually">Annually</option>
                  <option value="At Maturity">At Maturity</option>
                </select>
                {errors.couponFrequency && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.couponFrequency.message}
                  </p>
                )}
              </div>

              {/* Credit Rating */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Credit Rating (Optional)
                </label>
                <select
                  {...register("creditRating")}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select rating</option>
                  <option value="AAA">AAA</option>
                  <option value="AA">AA</option>
                  <option value="A">A</option>
                  <option value="BBB">BBB</option>
                  <option value="BB">BB</option>
                  <option value="B">B</option>
                  <option value="CCC">CCC</option>
                  <option value="Unrated">Unrated</option>
                </select>
              </div>
            </div>

            {/* Supporting Documents (read-only existing + optional new uploads) */}
            <div className="mt-6 space-y-4">
              <h3 className="text-lg font-semibold text-secondary-900">
                Supporting Documents
              </h3>

              {/* Default Risk Assessment Document */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Default Risk Assessment Document
                </label>
                <div className="p-4 space-y-2 border-2 border-dashed rounded-lg border-light-gray">
                  {/* Existing files from backend */}
                  {existingDocuments.length > 0 &&
                    (() => {
                      const riskDoc = existingDocuments.find(
                        (f) =>
                          (f?.metadata?.category || f?.category) ===
                          "riskAssessment"
                      );
                      if (!riskDoc) return null;
                      return (
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-2">
                            <Upload className="w-4 h-4 text-primary" />
                            <span className="max-w-xs font-medium truncate text-secondary-900">
                              {riskDoc.originalName ||
                                riskDoc.filename ||
                                "Risk Assessment Document"}
                            </span>
                          </div>
                          {renderDocumentActions(riskDoc, "xs")}
                        </div>
                      );
                    })()}

                  {/* New upload (optional replacement) */}
                  <div className="flex items-center justify-between mt-2">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => handleFileUpload(e, "riskAssessment")}
                      className="hidden"
                      id="risk-assessment-edit"
                    />
                    <label
                      htmlFor="risk-assessment-edit"
                      className="px-3 py-1 text-xs border rounded cursor-pointer bg-primary-50 text-primary-600 hover:bg-primary-100 border-primary-200"
                    >
                      {uploadedFiles.riskAssessment
                        ? "Replace"
                        : "Upload replacement"}
                    </label>
                    {uploadedFiles.riskAssessment && (
                      <div className="flex items-center space-x-2 text-xs">
                        <span className="max-w-xs truncate text-secondary-700">
                          {uploadedFiles.riskAssessment.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleFileDelete("riskAssessment")}
                          className="underline text-error-600 hover:text-error-700"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Legal Documents */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Relevant Legal Documents
                </label>
                <div className="p-4 space-y-2 border-2 border-dashed rounded-lg border-light-gray">
                  {existingDocuments.length > 0 &&
                    (() => {
                      const legalDocs = existingDocuments.filter(
                        (f) =>
                          (f?.metadata?.category || f?.category) ===
                          "legalDocuments"
                      );
                      if (!legalDocs.length) return null;
                      return (
                        <ul className="space-y-1 text-sm">
                          {legalDocs.map((doc, index) => (
                            <li
                              key={
                                doc._id ||
                                doc.fileId ||
                                `${doc?.metadata?.category ||
                                doc?.category ||
                                "legalDocuments"
                                }-${index}`
                              }
                              className="flex items-center justify-between"
                            >
                              <div className="flex items-center space-x-2">
                                <Upload className="w-4 h-4 text-primary" />
                                <span className="max-w-xs font-medium truncate text-secondary-900">
                                  {doc.originalName ||
                                    doc.filename ||
                                    "Legal Document"}
                                </span>
                              </div>
                              {renderDocumentActions(doc, "xs")}
                            </li>
                          ))}
                        </ul>
                      );
                    })()}

                  <div className="flex items-center justify-between mt-2">
                    <input
                      type="file"
                      accept=".pdf"
                      multiple
                      onChange={(e) => handleFileUpload(e, "legalDocuments")}
                      className="hidden"
                      id="legal-documents-edit"
                    />
                    <label
                      htmlFor="legal-documents-edit"
                      className="px-3 py-1 text-xs border rounded cursor-pointer bg-primary-50 text-primary-600 hover:bg-primary-100 border-primary-200"
                    >
                      {uploadedFiles.legalDocuments
                        ? "Replace"
                        : "Upload additional"}
                    </label>
                    {uploadedFiles.legalDocuments && (
                      <div className="flex items-center space-x-2 text-xs">
                        <span className="max-w-xs truncate text-secondary-700">
                          {Array.isArray(uploadedFiles.legalDocuments)
                            ? uploadedFiles.legalDocuments
                              .map((f) => f.name)
                              .join(", ")
                            : uploadedFiles.legalDocuments.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleFileDelete("legalDocuments")}
                          className="underline text-error-600 hover:text-error-700"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Covenants Documents */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Covenants Documents
                </label>
                <div className="p-4 space-y-2 border-2 border-dashed rounded-lg border-light-gray">
                  {existingDocuments.length > 0 &&
                    (() => {
                      const covDoc = existingDocuments.find(
                        (f) =>
                          (f?.metadata?.category || f?.category) ===
                          "covenantsDocuments"
                      );
                      if (!covDoc) return null;
                      return (
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-2">
                            <Upload className="w-4 h-4 text-primary" />
                            <span className="max-w-xs font-medium truncate text-secondary-900">
                              {covDoc.originalName ||
                                covDoc.filename ||
                                "Covenants Document"}
                            </span>
                          </div>
                          {renderDocumentActions(covDoc, "xs")}
                        </div>
                      );
                    })()}

                  <div className="flex items-center justify-between mt-2">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) =>
                        handleFileUpload(e, "covenantsDocuments")
                      }
                      className="hidden"
                      id="covenants-documents-edit"
                    />
                    <label
                      htmlFor="covenants-documents-edit"
                      className="px-3 py-1 text-xs border rounded cursor-pointer bg-primary-50 text-primary-600 hover:bg-primary-100 border-primary-200"
                    >
                      {uploadedFiles.covenantsDocuments
                        ? "Replace"
                        : "Upload replacement"}
                    </label>
                    {uploadedFiles.covenantsDocuments && (
                      <div className="flex items-center space-x-2 text-xs">
                        <span className="max-w-xs truncate text-secondary-700">
                          {uploadedFiles.covenantsDocuments.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleFileDelete("covenantsDocuments")}
                          className="underline text-error-600 hover:text-error-700"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={handlePrevious}
                className="flex items-center btn-secondary"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleSubmit(handleSaveAndExit)}
                  className="flex items-center btn-secondary"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save & Exit
                </button>
                <button
                  type="submit"
                  disabled={updateBondMutation.isPending}
                  className="flex items-center px-6 py-3 transition-colors border rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{
                    fontFamily: '"Roboto", sans-serif',
                    fontWeight: 700,
                    backgroundColor: updateBondMutation.isPending
                      ? "var(--color-medium-gray)"
                      : "var(--color-primary)",
                    borderColor: updateBondMutation.isPending
                      ? "var(--color-medium-gray)"
                      : "var(--color-primary)",
                    color: updateBondMutation.isPending
                      ? "var(--color-dark-gray)"
                      : "white",
                    cursor: updateBondMutation.isPending
                      ? "not-allowed"
                      : "pointer",
                    opacity: updateBondMutation.isPending ? 0.8 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (updateBondMutation.isPending) return;
                    e.target.style.backgroundColor = "var(--color-white)";
                    e.target.style.color = "var(--color-primary)";
                    e.target.style.borderColor = "var(--color-primary)";
                  }}
                  onMouseLeave={(e) => {
                    if (updateBondMutation.isPending) return;
                    e.target.style.backgroundColor = "var(--color-primary)";
                    e.target.style.color = "white";
                    e.target.style.borderColor = "var(--color-primary)";
                  }}
                >
                  {updateBondMutation.isPending ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const renderSukukStep = () => (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8 text-center">
        <h2 className="mb-4 text-3xl font-bold text-secondary-900">
          Sukuk Details
        </h2>
        <p className="text-lg text-secondary-600">
          Update the structure, asset, and compliance information for your Sukuk
        </p>
      </div>

      <form onSubmit={handleSubmit(handleNext)} className="space-y-6">
        {/* Sukuk Structure & Profit */}
        <div className="p-6 bg-white border rounded-lg border-secondary-200">
          <h3 className="mb-4 text-lg font-semibold text-secondary-900">
            Sukuk Structure & Profit
          </h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Profit Rate */}
            <div>
              <label className="block mb-2 text-sm font-medium text-dark-gray">
                Profit rate/discount/floating rate *
              </label>
              <input
                type="number"
                step="0.01"
                onKeyDown={(e) => {
                  if (e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }}
                {...register("profitRate", {
                  required: "Profit rate is required",
                  min: { value: 0, message: "Rate cannot be negative" },
                  max: { value: 100, message: "Rate cannot exceed 100%" },
                })}
                className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                placeholder="5.0"
              />
              {errors.profitRate && (
                <p className="mt-1 text-sm text-error-600">
                  {errors.profitRate.message}
                </p>
              )}
            </div>

            {/* Sukuk Structure Type */}
            <div>
              <label className="block mb-2 text-sm font-medium text-dark-gray">
                Sukuk Structure Type *
              </label>
              <select
                {...register("sukukStructureType", {
                  required: "Structure type is required",
                })}
                className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select structure</option>
                <option value="Ijarah">Sukuk Ijarah</option>
                <option value="Murabahah">Sukuk Murabahah</option>
                <option value="Musharakah">Sukuk Musharakah</option>
                <option value="Wakalah">Sukuk Wakalah</option>
                <option value="Other">Other</option>
              </select>
              {errors.sukukStructureType && (
                <p className="mt-1 text-sm text-error-600">
                  {errors.sukukStructureType.message}
                </p>
              )}
            </div>

            {/* Profit Distribution Frequency */}
            <div>
              <label className="block mb-2 text-sm font-medium text-dark-gray">
                Profit Distribution Frequency *
              </label>
              <select
                {...register("couponFrequency", {
                  required: "Distribution frequency is required",
                })}
                className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select frequency</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Semi-Annually">Semi-Annually</option>
                <option value="Annually">Annually</option>
                <option value="At Maturity">At Maturity</option>
              </select>
              {errors.couponFrequency && (
                <p className="mt-1 text-sm text-error-600">
                  {errors.couponFrequency.message}
                </p>
              )}
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-dark-gray">
                Islamic Financing Rating *
              </label>
              <select
                {...register("islamicFinancingRating", {
                  required: "Islamic financing rating is required",
                })}
                className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select rating</option>
                <option value="AAA">AAA</option>
                <option value="AA+">AA+</option>
                <option value="AA">AA</option>
                <option value="AA-">AA-</option>
                <option value="A+">A+</option>
                <option value="A">A</option>
                <option value="A-">A-</option>
                <option value="BBB+">BBB+</option>
                <option value="BBB">BBB</option>
                <option value="BBB-">BBB-</option>
              </select>
              {errors.islamicFinancingRating && (
                <p className="mt-1 text-sm text-error-600">
                  {errors.islamicFinancingRating.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Asset Information */}
        <div className="p-6 bg-white border rounded-lg border-secondary-200">
          <h3 className="mb-4 text-lg font-semibold text-secondary-900">
            Underlying Asset Information
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Sukuk Asset Type *
                </label>
                <select
                  {...register("sukukAssetType", {
                    required: "Sukuk asset type is required",
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select asset type</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Infrastructure">Infrastructure</option>
                  <option value="Equipment">Equipment</option>
                  <option value="Commodities">Commodities</option>
                  <option value="Other">Other</option>
                </select>
                {errors.sukukAssetType && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.sukukAssetType.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Asset Location *
                </label>
                <input
                  type="text"
                  {...register("assetLocation", {
                    required: "Asset location is required",
                  })}
                  className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Location of the underlying asset"
                />
                {errors.assetLocation && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.assetLocation.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-dark-gray">
                Underlying Asset Description *
              </label>
              <textarea
                {...register("assetDescription", {
                  required: "Asset description is required",
                })}
                rows={4}
                className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Detailed description of the asset supporting the Sukuk"
              />
              {errors.assetDescription && (
                <p className="mt-1 text-sm text-error-600">
                  {errors.assetDescription.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Shariah Compliance */}
        <div className="p-6 bg-white border rounded-lg border-secondary-200">
          <h3 className="mb-4 text-lg font-semibold text-secondary-900">
            Shariah Compliance
          </h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="block mb-2 text-sm font-medium text-dark-gray">
                Shariah Board Name *
              </label>
              <input
                type="text"
                {...register("shariahBoardName", {
                  required: "Shariah board name is required",
                })}
                className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Name of the Shariah advisory board"
              />
              {errors.shariahBoardName && (
                <p className="mt-1 text-sm text-error-600">
                  {errors.shariahBoardName.message}
                </p>
              )}
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-dark-gray">
                Fatwa Reference Number *
              </label>
              <input
                type="text"
                {...register("fatwaReference", {
                  required: "Fatwa reference is required",
                })}
                className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Fatwa reference number"
              />
              {errors.fatwaReference && (
                <p className="mt-1 text-sm text-error-600">
                  {errors.fatwaReference.message}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            onClick={handlePrevious}
            className="flex items-center btn-secondary"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </button>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleSubmit(handleSaveAndExit)}
              className="flex items-center btn-secondary"
            >
              <Save className="w-4 h-4 mr-2" />
              Save & Exit
            </button>
            <button
              type="submit"
              disabled={updateBondMutation.isPending}
              className="flex items-center px-6 py-3 transition-colors border rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{
                fontFamily: '"Roboto", sans-serif',
                fontWeight: 700,
                backgroundColor: updateBondMutation.isPending
                  ? "var(--color-medium-gray)"
                  : "var(--color-primary)",
                borderColor: updateBondMutation.isPending
                  ? "var(--color-medium-gray)"
                  : "var(--color-primary)",
                color: updateBondMutation.isPending
                  ? "var(--color-dark-gray)"
                  : "white",
                cursor: updateBondMutation.isPending
                  ? "not-allowed"
                  : "pointer",
                opacity: updateBondMutation.isPending ? 0.8 : 1,
              }}
              onMouseEnter={(e) => {
                if (updateBondMutation.isPending) return;
                e.target.style.backgroundColor = "var(--color-white)";
                e.target.style.color = "var(--color-primary)";
                e.target.style.borderColor = "var(--color-primary)";
              }}
              onMouseLeave={(e) => {
                if (updateBondMutation.isPending) return;
                e.target.style.backgroundColor = "var(--color-primary)";
                e.target.style.color = "white";
                e.target.style.borderColor = "var(--color-primary)";
              }}
            >
              {updateBondMutation.isPending ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );

  const renderStep4 = () => (
    <div className="max-w-4xl mx-auto">
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-medium text-dark-gray">
            Token Information
          </h2>
          <p className="text-sm text-secondary-600">
            {tokenLocked
              ? "Token parameters are immutable after creation."
              : "Provide or update the technical details; the token will be created on submission."}
          </p>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit(handleNext)} className="space-y-6">
            <h3 className="mb-4 text-lg font-semibold text-secondary-900">
              Token Details
            </h3>
            {tokenLocked && (
              <div className="p-4 mb-6 text-sm text-blue-700 border border-blue-200 rounded-lg bg-blue-50">
                Token already created on-chain. Fields are read-only. To change
                token economics you must create a new bond.
              </div>
            )}
            {!tokenLocked && (
              <div className="p-4 mb-6 text-sm text-yellow-700 border border-yellow-200 rounded-lg bg-yellow-50">
                Complete these details; the token will be created when you
                submit for approval. Once created, values become immutable.
              </div>
            )}

            {/* Token creation status banners if partially created */}
            {!tokenLocked &&
              bondData?.tokenDetails?.tokenCreationStatus === "failed" && (
                <div className="p-4 mb-6 text-sm text-red-700 border border-red-200 rounded-lg bg-red-50">
                  Token creation previously failed:{" "}
                  {bondData.tokenDetails.tokenCreationError || "Unknown error"}.
                  Adjust details and try again.
                </div>
              )}
            {tokenLocked && bondData?.tokenDetails?.tokenAddress && (
              <div className="p-4 mb-6 text-sm text-green-700 border border-green-200 rounded-lg bg-green-50">
                <p className="font-medium">Token Address:</p>
                <p className="font-mono text-xs break-all">
                  {bondData.tokenDetails.tokenAddress}
                </p>
                {bondData.tokenDetails.tokenTransactionHash && (
                  <p className="mt-2">
                    <span className="font-medium">Tx Hash:</span>{" "}
                    <span className="font-mono text-xs break-all">
                      {bondData.tokenDetails.tokenTransactionHash}
                    </span>
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {/* Wallet Selection or Display */}
              <div className="md:col-span-2">
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  {tokenLocked
                    ? "Wallet Address"
                    : "Wallet for Token Creation *"}
                </label>
                {tokenLocked ? (
                  <input
                    type="text"
                    value={
                      bondData?.tokenDetails?.wallet ||
                      bondData?.tokenDetails?.walletAddress ||
                      ""
                    }
                    readOnly
                    className="w-full px-3 py-2 font-mono text-xs bg-gray-100 border rounded-md cursor-not-allowed border-light-gray"
                  />
                ) : (
                  <div className="space-y-2">
                    <select
                      {...register("tokenWallet", {
                        required: "Please select a wallet for token creation",
                      })}
                      disabled={walletLoading || showTokenLoading}
                      className="w-full px-3 py-2 border rounded-md border-light-gray focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">
                        {walletLoading
                          ? "Loading wallet..."
                          : "Select existing wallet"}
                      </option>
                      {walletData?.data?.wallet && (
                        <option value={walletData.data.wallet.walletAddress}>
                          {`${walletData.data.wallet.walletAddress.slice(
                            0,
                            6
                          )}...${walletData.data.wallet.walletAddress.slice(
                            -4
                          )}`}{" "}
                          ({walletData.data.wallet.walletType} -{" "}
                          {walletData.data.wallet.status})
                        </option>
                      )}
                      {walletError && (
                        <option value="" disabled>
                          Error loading wallet
                        </option>
                      )}
                    </select>
                    {errors.tokenWallet && (
                      <p className="text-sm text-error-600">
                        {errors.tokenWallet.message}
                      </p>
                    )}
                    {!walletLoading &&
                      !walletError &&
                      !walletData?.data?.wallet && (
                        <p className="text-sm text-warning-600">
                          No wallet found. Create one first.
                        </p>
                      )}
                    <p className="text-xs text-secondary-500">
                      Selected wallet will be used to create and manage the bond
                      token. Ensure sufficient funds for gas.
                    </p>
                  </div>
                )}
              </div>
              {/* Token Name */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Token Name *
                </label>
                <input
                  type="text"
                  {...register("tokenName", {
                    required: "Token name is required",
                    minLength: {
                      value: 3,
                      message: "Token name must be at least 3 characters",
                    },
                  })}
                  readOnly={tokenLocked || showTokenLoading}
                  className={`w-full px-3 py-2 border border-light-gray rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${tokenLocked || showTokenLoading
                      ? "bg-gray-100 cursor-not-allowed"
                      : ""
                    }`}
                  placeholder="e.g., ABC Token"
                />
                {errors.tokenName && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.tokenName.message}
                  </p>
                )}
              </div>

              {/* Symbol */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Symbol *
                </label>
                <input
                  type="text"
                  {...register("tokenSymbol", {
                    required: "Token symbol is required",
                    minLength: {
                      value: 3,
                      message: "Symbol must be at least 3 characters",
                    },
                    maxLength: {
                      value: 6,
                      message: "Symbol must be at most 6 characters",
                    },
                  })}
                  readOnly={tokenLocked || showTokenLoading}
                  className={`w-full px-3 py-2 border border-light-gray rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${tokenLocked || showTokenLoading
                      ? "bg-gray-100 cursor-not-allowed"
                      : ""
                    }`}
                  placeholder="e.g., ABT"
                  style={{ textTransform: "uppercase" }}
                  onInput={(e) =>
                    (e.target.value = e.target.value.toUpperCase())
                  }
                />
                {errors.tokenSymbol && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.tokenSymbol.message}
                  </p>
                )}
              </div>

              {/* Decimal */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Decimal *
                </label>
                <input
                  type="number"
                  onKeyDown={(e) => {
                    if (e.key === '.' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                      e.preventDefault();
                    }
                  }}
                  {...register("tokenDecimals", {
                    required: "Token decimals are required",
                    min: { value: 0, message: "Minimum decimals is 0" },
                    max: { value: 18, message: "Maximum decimals is 18" },
                  })}
                  readOnly={tokenLocked || showTokenLoading}
                  className={`w-full px-3 py-2 border border-light-gray rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${tokenLocked || showTokenLoading
                      ? "bg-gray-100 cursor-not-allowed"
                      : ""
                    }`}
                  placeholder="18"
                />
                {errors.tokenDecimals && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.tokenDecimals.message}
                  </p>
                )}
              </div>

              {/* Face Value per Token */}
              <div>
                <label className="block mb-2 text-sm font-medium text-dark-gray">
                  Face Value per Token *
                </label>
                <input
                  type="number"
                  step="0.01"
                  onKeyDown={(e) => {
                    if (e.key === 'e' || e.key === 'E') {
                      e.preventDefault();
                    }
                  }}
                  {...register("faceValuePerToken", {
                    required: "Face value per token is required",
                    min: { value: 0.01, message: "Minimum face value is 0.01" },
                  })}
                  readOnly={tokenLocked || showTokenLoading}
                  className={`w-full px-3 py-2 border border-light-gray rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${tokenLocked || showTokenLoading
                      ? "bg-gray-100 cursor-not-allowed"
                      : ""
                    }`}
                  placeholder="1000.00"
                />
                {errors.faceValuePerToken && (
                  <p className="mt-1 text-sm text-error-600">
                    {errors.faceValuePerToken.message}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={handlePrevious}
                className="flex items-center btn-secondary"
                disabled={showTokenLoading}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleSubmit(handleSaveAndExit)}
                  className="flex items-center btn-secondary"
                  disabled={showTokenLoading}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save & Exit
                </button>
                <button
                  type="submit"
                  disabled={updateBondMutation.isPending || showTokenLoading}
                  className="flex items-center px-6 py-3 transition-colors border rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{
                    fontFamily: '"Roboto", sans-serif',
                    fontWeight: 700,
                    backgroundColor:
                      updateBondMutation.isPending || showTokenLoading
                        ? "var(--color-medium-gray)"
                        : "var(--color-primary)",
                    borderColor:
                      updateBondMutation.isPending || showTokenLoading
                        ? "var(--color-medium-gray)"
                        : "var(--color-primary)",
                    color:
                      updateBondMutation.isPending || showTokenLoading
                        ? "var(--color-dark-gray)"
                        : "white",
                    cursor:
                      updateBondMutation.isPending || showTokenLoading
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      updateBondMutation.isPending || showTokenLoading
                        ? 0.8
                        : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (updateBondMutation.isPending || showTokenLoading)
                      return;
                    e.target.style.backgroundColor = "var(--color-white)";
                    e.target.style.color = "var(--color-primary)";
                    e.target.style.borderColor = "var(--color-primary)";
                  }}
                  onMouseLeave={(e) => {
                    if (updateBondMutation.isPending || showTokenLoading)
                      return;
                    e.target.style.backgroundColor = "var(--color-primary)";
                    e.target.style.color = "white";
                    e.target.style.borderColor = "var(--color-primary)";
                  }}
                >
                  {updateBondMutation.isPending || showTokenLoading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">
                        {tokenLocked
                          ? "Updating..."
                          : "Saving Token Details..."}
                      </span>
                    </>
                  ) : (
                    <>
                      {tokenLocked ? "Next" : "Continue"}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => {
    const scriplessFlagValue = watch("scriplessFlag");
    const locationCodeValue = watch("locationCode");

    return (
      <div className="max-w-4xl mx-auto">
        <div className="card">
          <div className="card-header">
            <h2 className="mb-4 text-3xl font-bold text-secondary-900">
              Review & Save Changes
            </h2>
            <p className="text-lg text-secondary-600">
              Please carefully review all information before saving
            </p>
          </div>

          <div className="card-body">
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="p-6 bg-white border rounded-lg border-light-gray">
                <h3 className="mb-4 text-lg font-semibold text-secondary-900">
                  Bond Summary
                </h3>
                <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                  <div>
                    <span className="text-secondary-600">Bond Name:</span>
                    <span className="ml-2 font-medium">
                      {watch("bondName")}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Location Code:</span>
                    <span className="ml-2 font-medium">
                      {locationCodeValue || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Bond Type:</span>
                    <span className="ml-2 font-medium">
                      {selectedProductType === "fixed_notes"
                        ? "Conventional"
                        : "Sukuk"}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">ESG Bond:</span>
                    <span className="ml-2 font-medium">
                      {(() => {
                        const esg = watch("isESGBond");
                        return esg === true || esg === "true"
                          ? "Yes"
                          : esg === false || esg === "false"
                            ? "No"
                            : "—";
                      })()}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">
                      Scripless Issuance:
                    </span>
                    <span className="ml-2 font-medium">
                      {scriplessFlagValue === undefined
                        ? "—"
                        : scriplessFlagValue
                          ? "Yes"
                          : "No"}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Issue Amount:</span>
                    <span className="ml-2 font-medium">
                      MYR {watch("issueAmount")?.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Issue Date:</span>
                    <span className="ml-2 font-medium">
                      {watch("issueDate")}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Maturity Date:</span>
                    <span className="ml-2 font-medium">
                      {watch("maturityDate")}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Rate:</span>
                    <span className="ml-2 font-medium">
                      {selectedProductType === "fixed_notes"
                        ? watch("couponRate")
                        : watch("profitRate")}
                      %
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Frequency:</span>
                    <span className="ml-2 font-medium">
                      {watch("couponFrequency")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Uploaded Documents Summary */}
              <div className="p-6 bg-white border rounded-lg border-light-gray">
                <h3 className="mb-4 text-lg font-semibold text-secondary-900">
                  Uploaded Documents
                </h3>
                <div className="space-y-3">
                  {/* Required Documents */}
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-dark-green">
                      Required Documents
                    </h4>
                    <div className="space-y-2 text-sm">
                      {selectedProductType === "fixed_notes" && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-secondary-600">
                              Risk Assessment Report:
                            </span>
                            <span
                              className={
                                uploadedFiles.riskAssessment ||
                                  existingDocuments.find(
                                    (f) =>
                                      (f?.metadata?.category || f?.category) ===
                                      "riskAssessment"
                                  )
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {uploadedFiles.riskAssessment ||
                                existingDocuments.find(
                                  (f) =>
                                    (f?.metadata?.category || f?.category) ===
                                    "riskAssessment"
                                )
                                ? "✓ Uploaded"
                                : "✗ Missing"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-secondary-600">
                              Legal Documents:
                            </span>
                            <span
                              className={
                                uploadedFiles.legalDocuments ||
                                  existingDocuments.find(
                                    (f) =>
                                      (f?.metadata?.category || f?.category) ===
                                      "legalDocuments"
                                  )
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {uploadedFiles.legalDocuments ||
                                existingDocuments.find(
                                  (f) =>
                                    (f?.metadata?.category || f?.category) ===
                                    "legalDocuments"
                                )
                                ? "✓ Uploaded"
                                : "✗ Missing"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-secondary-600">
                              Covenants Documents:
                            </span>
                            <span
                              className={
                                uploadedFiles.covenantsDocuments ||
                                  existingDocuments.find(
                                    (f) =>
                                      (f?.metadata?.category || f?.category) ===
                                      "covenantsDocuments"
                                  )
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {uploadedFiles.covenantsDocuments ||
                                existingDocuments.find(
                                  (f) =>
                                    (f?.metadata?.category || f?.category) ===
                                    "covenantsDocuments"
                                )
                                ? "✓ Uploaded"
                                : "✗ Missing"}
                            </span>
                          </div>
                          {watch("isESGBond") === true && (
                            <div className="flex items-center justify-between">
                              <span className="text-secondary-600">
                                ESG Certification:
                              </span>
                              <span
                                className={
                                  uploadedFiles.esgCertification ||
                                    existingDocuments.find(
                                      (f) =>
                                        (f?.metadata?.category || f?.category) ===
                                        "esgCertification"
                                    )
                                    ? "text-green-600"
                                    : "text-gray-500"
                                }
                              >
                                {uploadedFiles.esgCertification ||
                                  existingDocuments.find(
                                    (f) =>
                                      (f?.metadata?.category || f?.category) ===
                                      "esgCertification"
                                  )
                                  ? "✓ Uploaded"
                                  : "Not provided"}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                      {selectedProductType === "sukuk" && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-secondary-600">
                              Asset Valuation Report:
                            </span>
                            <span
                              className={
                                uploadedFiles.valuationReport ||
                                  existingDocuments.find(
                                    (f) =>
                                      (f?.metadata?.category || f?.category) ===
                                      "valuationReport"
                                  )
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {uploadedFiles.valuationReport ||
                                existingDocuments.find(
                                  (f) =>
                                    (f?.metadata?.category || f?.category) ===
                                    "valuationReport"
                                )
                                ? "✓ Uploaded"
                                : "✗ Missing"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-secondary-600">
                              Legal Documents:
                            </span>
                            <span
                              className={
                                uploadedFiles.legalDocuments ||
                                  existingDocuments.find(
                                    (f) =>
                                      (f?.metadata?.category || f?.category) ===
                                      "legalDocuments"
                                  )
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {uploadedFiles.legalDocuments ||
                                existingDocuments.find(
                                  (f) =>
                                    (f?.metadata?.category || f?.category) ===
                                    "legalDocuments"
                                )
                                ? "✓ Uploaded"
                                : "✗ Missing"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-secondary-600">
                              Covenants Documents:
                            </span>
                            <span
                              className={
                                uploadedFiles.covenantsDocuments ||
                                  existingDocuments.find(
                                    (f) =>
                                      (f?.metadata?.category || f?.category) ===
                                      "covenantsDocuments"
                                  )
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {uploadedFiles.covenantsDocuments ||
                                existingDocuments.find(
                                  (f) =>
                                    (f?.metadata?.category || f?.category) ===
                                    "covenantsDocuments"
                                )
                                ? "✓ Uploaded"
                                : "✗ Missing"}
                            </span>
                          </div>
                          {watch("isESGBond") === true && (
                            <div className="flex items-center justify-between">
                              <span className="text-secondary-600">
                                ESG Certification:
                              </span>
                              <span
                                className={
                                  uploadedFiles.esgCertification ||
                                    existingDocuments.find(
                                      (f) =>
                                        (f?.metadata?.category || f?.category) ===
                                        "esgCertification"
                                    )
                                    ? "text-green-600"
                                    : "text-gray-500"
                                }
                              >
                                {uploadedFiles.esgCertification ||
                                  existingDocuments.find(
                                    (f) =>
                                      (f?.metadata?.category || f?.category) ===
                                      "esgCertification"
                                  )
                                  ? "✓ Uploaded"
                                  : "Not provided"}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Optional Documents */}
                  {selectedProductType === "sukuk" && (
                    <div>
                      <h4 className="mb-2 text-sm font-medium text-dark-green">
                        Optional Documents
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-secondary-600">
                            Shariah Compliance Certificate:
                          </span>
                          <span
                            className={
                              uploadedFiles.shariahCertificate ||
                                existingDocuments.find(
                                  (f) =>
                                    (f?.metadata?.category || f?.category) ===
                                    "shariahCertificate"
                                )
                                ? "text-green-600"
                                : "text-gray-500"
                            }
                          >
                            {uploadedFiles.shariahCertificate ||
                              existingDocuments.find(
                                (f) =>
                                  (f?.metadata?.category || f?.category) ===
                                  "shariahCertificate"
                              )
                              ? "✓ Uploaded"
                              : "Not provided"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-secondary-600">
                            Securities Lodgement Form:
                          </span>
                          <span
                            className={
                              uploadedFiles.securitiesLodgementForm ||
                                existingDocuments.find(
                                  (f) =>
                                    (f?.metadata?.category || f?.category) ===
                                    "securitiesLodgementForm"
                                )
                                ? "text-green-600"
                                : "text-gray-500"
                            }
                          >
                            {uploadedFiles.securitiesLodgementForm ||
                              existingDocuments.find(
                                (f) =>
                                  (f?.metadata?.category || f?.category) ===
                                  "securitiesLodgementForm"
                              )
                              ? "✓ Uploaded"
                              : "Not provided"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-secondary-600">
                            Global Certificate:
                          </span>
                          <span
                            className={
                              uploadedFiles.globalCertificate ||
                                existingDocuments.find(
                                  (f) =>
                                    (f?.metadata?.category || f?.category) ===
                                    "globalCertificate"
                                )
                                ? "text-green-600"
                                : "text-gray-500"
                            }
                          >
                            {uploadedFiles.globalCertificate ||
                              existingDocuments.find(
                                (f) =>
                                  (f?.metadata?.category || f?.category) ===
                                  "globalCertificate"
                              )
                              ? "✓ Uploaded"
                              : "Not provided"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-secondary-600">
                            Debt Facility / Securities Information Form:
                          </span>
                          <span
                            className={
                              uploadedFiles.debtFacilityForm ||
                                existingDocuments.find(
                                  (f) =>
                                    (f?.metadata?.category || f?.category) ===
                                    "debtFacilityForm"
                                )
                                ? "text-green-600"
                                : "text-gray-500"
                            }
                          >
                            {uploadedFiles.debtFacilityForm ||
                              existingDocuments.find(
                                (f) =>
                                  (f?.metadata?.category || f?.category) ===
                                  "debtFacilityForm"
                              )
                              ? "✓ Uploaded"
                              : "Not provided"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="flex items-center px-6 py-3 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                  style={{
                    fontFamily: '"Roboto", sans-serif',
                    fontWeight: 700,
                    backgroundColor: "var(--color-gray)",
                    color: "var(--color-dark-gray)",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "var(--color-medium-gray)";
                    e.target.style.color = "var(--color-dark-gray)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "var(--color-gray)";
                    e.target.style.color = "var(--color-dark-gray)";
                  }}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </button>

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/issuer/bonds/${bondId}`)}
                    className="flex items-center px-6 py-3 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                    style={{
                      fontFamily: '"Roboto", sans-serif',
                      fontWeight: 700,
                      backgroundColor: "var(--color-gray)",
                      color: "var(--color-dark-gray)",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor =
                        "var(--color-medium-gray)";
                      e.target.style.color = "var(--color-dark-gray)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "var(--color-gray)";
                      e.target.style.color = "var(--color-dark-gray)";
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit(handleSaveAndExit)}
                    disabled={updateBondMutation.isPending}
                    className="flex items-center px-6 py-3 transition-colors border rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
                    style={{
                      fontFamily: '"Roboto", sans-serif',
                      fontWeight: 700,
                      backgroundColor: updateBondMutation.isPending
                        ? "var(--color-medium-gray)"
                        : "var(--color-gray)",
                      borderColor: updateBondMutation.isPending
                        ? "var(--color-medium-gray)"
                        : "var(--color-gray)",
                      color: "var(--color-dark-gray)",
                      cursor: updateBondMutation.isPending
                        ? "not-allowed"
                        : "pointer",
                      opacity: updateBondMutation.isPending ? 0.8 : 1,
                    }}
                    onMouseEnter={(e) => {
                      if (updateBondMutation.isPending) return;
                      e.target.style.backgroundColor =
                        "var(--color-medium-gray)";
                      e.target.style.color = "var(--color-dark-gray)";
                    }}
                    onMouseLeave={(e) => {
                      if (updateBondMutation.isPending) return;
                      e.target.style.backgroundColor = "var(--color-gray)";
                      e.target.style.color = "var(--color-dark-gray)";
                    }}
                  >
                    {updateBondMutation.isPending ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                  {canSubmitForApproval && (
                    <button
                      type="button"
                      onClick={handleSubmit(handleSubmitForApproval)}
                      disabled={
                        updateBondMutation.isPending ||
                        submitBondMutation.isPending
                      }
                      className="flex items-center px-6 py-3 transition-colors border rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
                      style={{
                        fontFamily: '"Roboto", sans-serif',
                        fontWeight: 700,
                        backgroundColor:
                          updateBondMutation.isPending ||
                            submitBondMutation.isPending
                            ? "var(--color-medium-gray)"
                            : "var(--color-primary)",
                        borderColor:
                          updateBondMutation.isPending ||
                            submitBondMutation.isPending
                            ? "var(--color-medium-gray)"
                            : "var(--color-primary)",
                        color:
                          updateBondMutation.isPending ||
                            submitBondMutation.isPending
                            ? "var(--color-dark-gray)"
                            : "white",
                        cursor:
                          updateBondMutation.isPending ||
                            submitBondMutation.isPending
                            ? "not-allowed"
                            : "pointer",
                        opacity:
                          updateBondMutation.isPending ||
                            submitBondMutation.isPending
                            ? 0.8
                            : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (
                          updateBondMutation.isPending ||
                          submitBondMutation.isPending
                        )
                          return;
                        e.target.style.backgroundColor = "var(--color-white)";
                        e.target.style.color = "var(--color-primary)";
                        e.target.style.borderColor = "var(--color-primary)";
                      }}
                      onMouseLeave={(e) => {
                        if (
                          updateBondMutation.isPending ||
                          submitBondMutation.isPending
                        )
                          return;
                        e.target.style.backgroundColor = "var(--color-primary)";
                        e.target.style.color = "white";
                        e.target.style.borderColor = "var(--color-primary)";
                      }}
                    >
                      {submitBondMutation.isPending ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Submit for Approval
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate(`/issuer/bonds/${bondId}`)}
                className="p-2 mr-4 text-gray-400 rounded-md hover:text-gray-600"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Edit Bond</h1>
                <p className="mt-2 text-sm text-gray-600">
                  Update details for:{" "}
                  <span className="font-medium">{bondData?.bondName}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {renderStepIndicator()}

        <div className="min-h-96">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
        </div>
      </div>
      {/* Token Details Saving Overlay (token creation deferred) */}
      {((updateBondMutation.isPending && currentStep === 4 && !tokenLocked) ||
        isTokenCreationInProgress ||
        submitBondMutation.isPending) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-full max-w-sm p-6 mx-4 text-center bg-white rounded-lg">
              <div className="flex justify-center mb-4">
                <LoadingSpinner />
              </div>
              {isTokenCreationInProgress ? (
                <>
                  <h3 className="text-lg font-medium text-gray-900">
                    Creating On-Chain Token...
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Your bond was submitted. Waiting for token deployment to
                    finalize.
                  </p>
                  <p className="mt-2 text-xs text-gray-500">
                    This may take up to a minute depending on network conditions.
                  </p>
                </>
              ) : submitBondMutation.isPending ? (
                <>
                  <h3 className="text-lg font-medium text-gray-900">
                    Submitting Bond...
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Please wait while we submit your bond for approval.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-gray-900">
                    Saving Token Details...
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Token will be created after you submit the bond for approval.
                  </p>
                </>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default EditBond;
